import { useState, useEffect, useCallback } from "react";
import {
  Mic,
  Wifi,
  Volume2,
  Monitor,
  EyeOff,
  KeyRound,
  CheckCircle2,
  Circle,
  RotateCcw,
} from "lucide-react";

const STORAGE_KEY = "demo_checklist_v1";

const CHECKLIST_ITEMS = [
  {
    id: "incognito",
    icon: EyeOff,
    label: "Browser in private/incognito mode",
    hint: "So an old student session doesn't auto-login on stage.",
  },
  {
    id: "audio",
    icon: Volume2,
    label: "Volume unmuted",
    hint: "Lesson videos + LemonBot sound effects play during the demo.",
  },
  {
    id: "display",
    icon: Monitor,
    label: "Projector / screen share connected",
    hint: "Mirror or extend before opening the login page.",
  },
  {
    id: "wifi",
    icon: Wifi,
    label: "Wi-Fi stable (no captive portal)",
    hint: "School networks often need a one-time browser sign-in first.",
  },
  {
    id: "creds",
    icon: KeyRound,
    label: "Login credentials handy",
    hint: "student-complete@test.com / student123 — click the Email tab first.",
  },
  {
    id: "disc",
    icon: Mic,
    label: "DISC quiz answers prepped",
    hint: "Optional — only if you'll demo the live DISC flow with a volunteer.",
  },
];

function loadChecks() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveChecks(checks) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(checks));
  } catch {
    /* ignore quota errors */
  }
}

function PreDemoChecklist({ onAllReady }) {
  const [checks, setChecks] = useState({});

  useEffect(() => {
    setChecks(loadChecks());
  }, []);

  const toggle = useCallback((id) => {
    setChecks((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      saveChecks(next);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setChecks({});
    saveChecks({});
  }, []);

  const total = CHECKLIST_ITEMS.length;
  const done = CHECKLIST_ITEMS.filter((it) => checks[it.id]).length;
  const allReady = done === total;
  const pct = Math.round((done / total) * 100);

  // Notify parent when state flips to "all ready"
  useEffect(() => {
    if (onAllReady) onAllReady(allReady);
  }, [allReady, onAllReady]);

  return (
    <div
      className="bg-white rounded-2xl border border-gray-200 p-6"
      data-testid="pre-demo-checklist"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <Mic className="w-4 h-4 text-fuchsia-500" />
            Pre-Demo Checklist
          </h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Run through this once before kicking off the reset — saved in your
            browser, clears when you reset it.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div
              className={`text-sm font-bold ${
                allReady ? "text-emerald-600" : "text-gray-700"
              }`}
              data-testid="checklist-progress-text"
            >
              {done} of {total} ready
            </div>
            <div className="text-[11px] text-gray-400">
              {allReady ? "All set 🎉" : `${pct}%`}
            </div>
          </div>
          <button
            onClick={reset}
            disabled={done === 0}
            data-testid="checklist-reset-btn"
            className="text-xs font-medium text-gray-500 hover:text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-50"
            title="Clear all checkboxes"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-4">
        <div
          className={`h-full transition-all duration-300 ${
            allReady ? "bg-emerald-500" : "bg-fuchsia-500"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>

      {/* Items */}
      <ul className="space-y-2">
        {CHECKLIST_ITEMS.map((item) => {
          const Icon = item.icon;
          const checked = !!checks[item.id];
          return (
            <li key={item.id}>
              <button
                onClick={() => toggle(item.id)}
                data-testid={`checklist-item-${item.id}`}
                className={`w-full flex items-start gap-3 text-left rounded-lg border p-3 transition-all ${
                  checked
                    ? "border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                {checked ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Icon
                      className={`w-4 h-4 flex-shrink-0 ${
                        checked ? "text-emerald-600" : "text-gray-400"
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        checked
                          ? "text-emerald-900 line-through decoration-emerald-300"
                          : "text-gray-800"
                      }`}
                    >
                      {item.label}
                    </span>
                  </div>
                  <p
                    className={`text-xs mt-0.5 ${
                      checked ? "text-emerald-700/70" : "text-gray-500"
                    }`}
                  >
                    {item.hint}
                  </p>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      {allReady && (
        <div
          className="mt-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800 flex items-center gap-2"
          data-testid="checklist-all-ready-banner"
        >
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" />
          <span>
            <strong>All set!</strong> Hit{" "}
            <span className="font-semibold">Reset &amp; Prepare Demo</span> below
            and you're ready to present.
          </span>
        </div>
      )}
    </div>
  );
}

export default PreDemoChecklist;
